import { createServerFn } from "@tanstack/react-start";
import {
  interactionAiQuestionSchema,
  REQUEST_LIMIT_PER_HOUR,
} from "./ai-interaction.schemas";

const MODEL = "openai/gpt-6-astra";

export type InteractionAiResult =
  | {
      status: "ok";
      answer: string;
      riskLevel: string;
      sources: { key: string; title: string; url: string | null }[];
      pair: { a: string; b: string };
    }
  | { status: "noAssessment"; pair: { a: string; b: string } }
  | { status: "limit" }
  | { status: "error"; reason: "credits" | "denied" | "config" | "unavailable" | "unverified" | "empty" };

type SourceRow = { key: string; title: string; url: string | null };

/** Builds the model context strictly from the stored pair data. No outside knowledge. */
export function buildPairContext(input: {
  a: string;
  b: string;
  riskLevel: string;
  markers: string[];
  texts: Record<string, string | null>;
  sources: SourceRow[];
}): string {
  const lines: string[] = [];
  lines.push(`Kombination: ${input.a} + ${input.b}`);
  lines.push(`Risikostufe (Datenbank): ${input.riskLevel}`);
  lines.push(`Marker: ${input.markers.length ? input.markers.join(", ") : "keine"}`);
  for (const [field, value] of Object.entries(input.texts)) {
    if (value && value.trim()) lines.push(`${field}: ${value.trim()}`);
  }
  if (input.sources.length) {
    lines.push("Quellen (nur diese Kuerzel zitieren):");
    for (const s of input.sources) {
      lines.push(`- [${s.key}] ${s.title}${s.url ? ` (${s.url})` : ""}`);
    }
  } else {
    lines.push("Quellen: keine verknuepften Quellen vorhanden.");
  }
  return lines.join("\n");
}

/** Card fields of interaction_details.content_de / content_en that carry readable text. */
const CONTENT_FIELDS: Array<{ key: string; label: string }> = [
  { key: "kernaussage", label: "Kernaussage" },
  { key: "was_passiert", label: "Was passiert" },
  { key: "fuer_wen", label: "Fuer wen relevant" },
  { key: "warnzeichen", label: "Warnzeichen" },
  { key: "warnzeichen_hinweis", label: "Hinweis zu Warnzeichen" },
  { key: "experte", label: "Fachliche Einordnung" },
  { key: "evidenz", label: "Evidenzlage" },
];

/** Internal citation markers such as [A1] belong to the card text, not to the linked source keys. */
function stripInlineMarkers(text: string): string {
  return text.replace(/\s*\[[A-Za-z]{1,3}\d{1,2}\]/g, "").replace(/[ \t]{2,}/g, " ").trim();
}

/** Turns a content_de / content_en jsonb object into plain labelled text for the model context. */
export function flattenContent(content: unknown): Record<string, string> {
  if (!content || typeof content !== "object" || Array.isArray(content)) return {};
  const source = content as Record<string, unknown>;
  const out: Record<string, string> = {};
  for (const field of CONTENT_FIELDS) {
    const value = source[field.key];
    const text = Array.isArray(value)
      ? value.filter((v): v is string => typeof v === "string").join(" ")
      : typeof value === "string"
        ? value
        : "";
    const clean = stripInlineMarkers(text);
    if (clean) out[field.label] = clean;
  }
  return out;
}

const SYSTEM_PROMPT_DE = `Du erklaerst Nutzern einer deutschen Harm-Reduction-Seite eine bereits vorhandene Bewertung einer Substanzkombination.
Strikte Regeln:
- Nutze ausschliesslich die Angaben im bereitgestellten Kontext. Kein Allgemeinwissen, keine Zahlen, Wirkstoffe oder Aussagen, die nicht im Kontext stehen.
- Keine Dosisempfehlung, kein Zeitabstand, keine Freigabe und keine Formulierung, die eine Kombination als sicher darstellt.
- Wenn die Frage aus dem Kontext nicht beantwortbar ist, sage das offen in einem Satz.
- Zitiere jede inhaltliche Aussage mit dem Quellenkuerzel in eckigen Klammern, zum Beispiel [tripsit]. Nutze nur Kuerzel aus dem Kontext.
- Antworte auf Deutsch mit echten Umlauten, klar und ohne Fachjargon, maximal 200 Woerter, keine Gedankenstriche.
- Schreibe reinen Text ohne Markdown, ohne Sternchen und ohne Ueberschriften.`;

const SYSTEM_PROMPT_EN = `You explain an existing assessment of a substance combination to readers of a harm reduction site.
Strict rules:
- Use only the provided context. No outside knowledge, no numbers or claims that are not in the context.
- No dose advice, no timing advice, no clearance, and never present a combination as safe.
- If the question cannot be answered from the context, say so plainly in one sentence.
- Cite every factual statement with the source key in square brackets, for example [tripsit]. Only keys from the context.
- Answer in English, clear and jargon free, at most 200 words.
- Plain text only, no markdown, no asterisks, no headings.`;

function hourWindowStart(now = new Date()): string {
  const d = new Date(now);
  d.setUTCMinutes(0, 0, 0);
  return d.toISOString();
}

async function visitorHash(): Promise<string> {
  const { getRequest } = await import("@tanstack/react-start/server");
  const request = getRequest();
  const headers = request.headers;
  const ip =
    headers.get("cf-connecting-ip") ??
    headers.get("x-forwarded-for")?.split(",")[0]?.trim() ??
    "unknown";
  const ua = headers.get("user-agent") ?? "unknown";
  const { createHash } = await import("node:crypto");
  return createHash("sha256").update(`${ip}|${ua}|restlicht-ai`).digest("hex").slice(0, 32);
}

export const askInteractionAi = createServerFn({ method: "POST" })
  .inputValidator((i: unknown) => interactionAiQuestionSchema.parse(i))
  .handler(async ({ data }): Promise<InteractionAiResult> => {
    const apiKey = process.env["LOVABLE_API_KEY"];
    if (!apiKey) return { status: "error", reason: "config" };

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const [lowId, highId] = [data.substanceAId, data.substanceBId].sort();

    const subs = await supabaseAdmin
      .from("substances")
      .select("id, name")
      .in("id", [data.substanceAId, data.substanceBId]);
    if (subs.error) return { status: "error", reason: "unavailable" };
    const nameOf = (id: string) => subs.data?.find((s) => s.id === id)?.name ?? "";
    const pair = { a: nameOf(data.substanceAId), b: nameOf(data.substanceBId) };
    if (!pair.a || !pair.b) return { status: "error", reason: "unavailable" };

    const detail = await supabaseAdmin
      .from("interaction_details")
      .select(
        "id, risk_level, markers, summary, mechanism, effects_combined, risks, harm_reduction, content_de, content_en, published",
      )
      .eq("substance_low_id", lowId!)
      .eq("substance_high_id", highId!)
      .eq("published", true)
      .maybeSingle();
    if (detail.error) return { status: "error", reason: "unavailable" };
    if (!detail.data) return { status: "noAssessment", pair };

    const links = await supabaseAdmin
      .from("interaction_sources")
      .select("sort_order, sources!inner(key, title, url, published)")
      .eq("interaction_id", detail.data.id)
      .order("sort_order", { ascending: true });
    const sources: SourceRow[] = (links.data ?? [])
      .map((row) => (row as unknown as { sources: { key: string; title: string; url: string | null; published: boolean } }).sources)
      .filter((s) => s && s.published)
      .map((s) => ({ key: s.key, title: s.title, url: s.url ?? null }));

    // Rate limit per anonymous visitor and hour.
    const hash = await visitorHash();
    const windowStart = hourWindowStart();
    const existing = await supabaseAdmin
      .from("interaction_ai_rate_limits")
      .select("id, request_count")
      .eq("visitor_hash", hash)
      .eq("window_start", windowStart)
      .maybeSingle();
    const used = existing.data?.request_count ?? 0;
    if (used >= REQUEST_LIMIT_PER_HOUR) return { status: "limit" };
    if (existing.data) {
      await supabaseAdmin
        .from("interaction_ai_rate_limits")
        .update({ request_count: used + 1 })
        .eq("id", existing.data.id);
    } else {
      await supabaseAdmin
        .from("interaction_ai_rate_limits")
        .insert({ visitor_hash: hash, window_start: windowStart, request_count: 1 });
    }

    const context = buildPairContext({
      a: pair.a,
      b: pair.b,
      riskLevel: detail.data.risk_level as string,
      markers: (detail.data.markers as string[] | null) ?? [],
      texts: {
        Kurzfassung: detail.data.summary,
        Mechanismus: detail.data.mechanism,
        "Kombinierte Wirkung": detail.data.effects_combined,
        Risiken: detail.data.risks,
        "Safer Use": detail.data.harm_reduction,
        ...flattenContent(data.lang === "en" ? detail.data.content_en : detail.data.content_de),
      },
      sources,
    });

    const { generateGatewayText, getLovableAiGatewayRunId } = await import("./ai-gateway.server");
    let incomingRunId: string | undefined;
    try {
      const { getRequest } = await import("@tanstack/react-start/server");
      incomingRunId = getLovableAiGatewayRunId(getRequest().headers);
    } catch {
      incomingRunId = undefined;
    }

    const result = await generateGatewayText({
      apiKey,
      model: MODEL,
      instructions: data.lang === "en" ? SYSTEM_PROMPT_EN : SYSTEM_PROMPT_DE,
      input: `Kontext:\n${context}\n\nFrage der Nutzerin oder des Nutzers:\n${data.question}`,
      reasoningEffort: "low",
      ...(incomingRunId ? { incomingRunId } : {}),
    });

    const logRow = {
      substance_a_id: data.substanceAId,
      substance_b_id: data.substanceBId,
      substance_a_name: pair.a,
      substance_b_name: pair.b,
      question: data.question,
      risk_level: detail.data.risk_level,
      source_keys: sources.map((s) => s.key),
      model: MODEL,
      visitor_hash: hash,
      lang: data.lang,
    };

    if (!result.ok) {
      const reason = result.reason === "limit" ? "unavailable" : result.reason;
      await supabaseAdmin.from("interaction_ai_queries").insert({
        ...logRow,
        answer: null,
        status: `error:${result.reason}`,
      });
      return { status: "error", reason };
    }

    if (!result.text) {
      await supabaseAdmin.from("interaction_ai_queries").insert({ ...logRow, answer: null, status: "error:empty" });
      return { status: "error", reason: "empty" };
    }

    const citesSource = sources.length === 0 || sources.some((s) => result.text.includes(`[${s.key}]`));
    if (!citesSource) {
      await supabaseAdmin.from("interaction_ai_queries").insert({
        ...logRow,
        answer: result.text,
        status: "error:unverified",
      });
      return { status: "error", reason: "unverified" };
    }

    await supabaseAdmin.from("interaction_ai_queries").insert({ ...logRow, answer: result.text, status: "ok" });

    return {
      status: "ok",
      answer: result.text,
      riskLevel: detail.data.risk_level as string,
      sources,
      pair,
    };
  });
